import { promise } from '@xmpp/events';
import { Element as XmlElement } from '@xmpp/xml';
import { ActionTree } from 'vuex';

import { makeXmppClient, getJidNode, getBareJid } from '@/modules/xmpp_client';

import { XmppClientError, XmppAuthenticationError } from './errors';
import { ClientStatus, StateType } from './state';

enum XmppStanza {
  MESSAGE = 'message',
  PRESENCE = 'presence',
}

let internalXmppClient: ReturnType<typeof makeXmppClient> | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const moduleActions: ActionTree<StateType, any> = {
  async restoreFromSession({ dispatch, rootGetters }) {
    const sessionId: string | null = rootGetters['apiSession/sessionId'];
    const sessionToken: string | null = rootGetters['apiSession/sessionToken'];
    const jidNode: string | null = rootGetters['apiSession/jidNode'];

    if (!sessionId || !sessionToken || !jidNode) {
      throw new XmppClientError('Session missing, cannot restore XMPP connection.');
    }

    await dispatch('initialize', { id: sessionId, jidNode: jidNode, token: sessionToken });
    await dispatch('connect');
  },

  initialize({ commit, dispatch, getters }, session: { id: string, jidNode: string, token: string }) {
    if (internalXmppClient) return;

    const url = getters.selectedXmppUrl;
    internalXmppClient = makeXmppClient(url, session);

    internalXmppClient.on('status', (status) => {
      // The list of all statuses is at: https://github.com/xmppjs/xmpp.js/tree/main/packages/client#status
      // TODO: Evaluate if it's worth tracking other statuses.
      if (status === 'online') {
        commit('setStatus', ClientStatus.CONNECTED);
      } else if (status === 'offline') {
        commit('setStatus', ClientStatus.INIT);
      } else {
        commit('setStatus', ClientStatus.CONNECTING);
      }
    });

    internalXmppClient.on('stanza', async (stanza) => {
      try {
        if (stanza.is(XmppStanza.MESSAGE)) {
          await dispatch('xmppMessaging/processIncoming', { xmppMessage: stanza }, { root: true });
        } else if (stanza.is(XmppStanza.PRESENCE)) {
          await dispatch('presenceHandler', stanza);
        }
      } catch (error) {
        // Catch
      }
    });
  },

  async connect({ commit, state }) {
    if (!internalXmppClient) {
      throw new XmppClientError('XMPP client not initialized');
    }

    if (state.status === ClientStatus.CONNECTED) {
      // Already connected. No need to do anything.
      return;
    }

    try {
      if (state.status === ClientStatus.CONNECTING) {
        // Wait until client is connected. Promise rejects on XMPP errors.
        await promise(internalXmppClient, 'online');
      } else {
        commit('setStatus', ClientStatus.CONNECTING);
        await internalXmppClient.start();
      }
    } catch (error) {
      throw new XmppAuthenticationError('Could not connect to XMPP', error);
    }
  },

  // TODO: Should this be here or in xmpp_messaging?
  async presenceHandler({ dispatch }, presence: XmlElement) {
    // Mostly legacy notifications
    // https://git.allm.net/medicalline/join-backend/join-rest-api/-/blob/develop/docs/xmpp_messages.adoc#user-content-presence-messages

    // The admin panel can "delete" groups. The group isn't actually deleted
    // because you can still pull its history and send messages etc.
    const serverEvent = presence.getChild('server-event');
    if (serverEvent?.getAttr('type') === 'group_deleted') {
      const groupJid = serverEvent.getAttr('source');
      if (groupJid) {
        await dispatch('groups/remove', getJidNode(groupJid), { root: true });
      }
      return;
    }

    const presenceType = presence.getAttr('type');

    if (presenceType === 'error') return; // Not entirely sure in which situations these errors can occur.
    if (presenceType === 'unavailable') return; // We usually get a barrage of these upon connection to XMPP.

    // Non-standard child elements.
    // This is just for compatibility with old functionality. REST API is
    // sending i18n messages instead.
    if (presence.getChild('nick') || presence.getChild('x')?.getChild('photo')) {
      const from = getBareJid(presence.getAttr('from') || '');
      await dispatch('users/refresh', getJidNode(from), { root: true });
    }
  },

  async teardown({ commit }) {
    await internalXmppClient?.stop();
    internalXmppClient = null;
    commit('setStatus', ClientStatus.INIT);
  },

  async send({ state }, message: XmlElement) {
    if (!internalXmppClient) {
      throw new XmppClientError('XMPP client not initialized');
    }

    if (state.status !== ClientStatus.CONNECTED) {
      throw new XmppClientError('Not connected to XMPP');
    }

    try {
      await internalXmppClient.send(message);
    } catch (error) {
      throw new XmppClientError('XMPP send failure', error);
    }
  },

  async sendIQ({ state }, { iq, timeout }: { iq: XmlElement, timeout?: number }) {
    if (!internalXmppClient) {
      throw new XmppClientError('XMPP client not initialized');
    }

    if (state.status !== ClientStatus.CONNECTED) {
      throw new XmppClientError('Not connected to XMPP');
    }

    try {
      return await internalXmppClient.iqCaller.request(iq, timeout);
    } catch (error) {
      throw new XmppClientError('XMPP sendIQ failure', error);
    }
  },
};

export default moduleActions;